import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useParams } from "react-router-dom";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";

const HEADER_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;
const ACCENT = COLORS.primary_color_shade5;
const ACCENT_DARK = COLORS.primary_color_shade7;
const ACCENT_SHADOW = "rgba(3, 169, 244, 0.28)";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const TYPE_THEME = {
  FEE: {
    accent: ACCENT,
    soft: "rgba(3, 169, 244, 0.12)",
    border: "rgba(3, 169, 244, 0.4)",
    label: "Fee",
    icon: "fa-check",
  },
  OLD_DUE: {
    accent: COLORS.card_red || "#ef5350",
    soft: "rgba(239, 83, 80, 0.12)",
    border: "rgba(239, 83, 80, 0.5)",
    label: "Old Due",
    icon: "fa-exclamation",
  },
  DEFAULT: {
    accent: ACCENT,
    soft: "rgba(3, 169, 244, 0.12)",
    border: "rgba(3, 169, 244, 0.4)",
    label: "Other",
    icon: "fa-rupee",
  },
};

function getTypeTheme(transactionFor) {
  return TYPE_THEME[transactionFor] || {
    ...TYPE_THEME.DEFAULT,
    label: transactionFor || "Payment",
  };
}

function formatAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `₹${amount ?? "0"}`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function classLabel(student) {
  const cls = student?.class?.[0];
  if (!cls) return "—";
  const name = cls.class_name || "";
  const section = cls.section_name;
  return section ? `${name}:${section}` : name;
}

function lineAmount(item, transactionFor) {
  if (transactionFor === "OLD_DUE") {
    return Number(item.amount_paid) || 0;
  }
  return Number(item.fee_paid) || 0;
}

const MetaChip = memo(function MetaChip({ children, tone = "neutral" }) {
  const tones = {
    neutral: {
      color: "#334155",
      background: "#f1f5f9",
      border: "1px solid #cbd5e1",
    },
    primary: {
      color: COLORS.white,
      background: ACCENT_DARK,
      border: "1px solid " + ACCENT_DARK,
    },
    green: {
      color: ACCENT_DARK,
      background: COLORS.primary_color_shade0,
      border: `1px solid ${COLORS.primary_color_shade3}`,
    },
    blue: {
      color: ACCENT_DARK,
      background: "rgba(3,169,244,0.12)",
      border: "1px solid rgba(3,169,244,0.35)",
    },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 11,
        fontWeight: 800,
        borderRadius: 999,
        padding: "2px 9px",
        ...t,
      }}
    >
      {children}
    </span>
  );
});

const StudentSummary = memo(function StudentSummary({ student }) {
  if (!student) return null;
  const rollNo = student?.class?.[0]?.student_roll_no ?? "—";
  const cls = classLabel(student);

  const tile = {
    padding: "5px 8px",
    borderRadius: 8,
    background: COLORS.white,
    border: "1.5px solid #cbd5e1",
    minWidth: 0,
  };
  const label = {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.35,
    textTransform: "uppercase",
    color: "#475569",
    lineHeight: 1.2,
  };
  const value = {
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <div
      style={{
        marginBottom: 14,
        borderRadius: 12,
        border: `2px solid ${ACCENT_DARK}`,
        overflow: "hidden",
        background: COLORS.white,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          background: `linear-gradient(90deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.white} 55%)`,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            padding: 3,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade8} 100%)`,
            boxShadow: `0 4px 12px ${ACCENT_SHADOW}`,
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              overflow: "hidden",
              border: "2.5px solid #fff",
              background: `linear-gradient(180deg, ${COLORS.primary_color_shade0} 0%, #e2e8f0 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="fa fa-user"
              aria-hidden
              style={{
                fontSize: 34,
                color: COLORS.primary_color_shade3,
                position: "absolute",
              }}
            />
            <img
              src={student.student_img_path}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1.2,
              marginBottom: 5,
            }}
          >
            {student.stu_name}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
            <MetaChip tone="primary">ID: {student.student_enrollment_no}</MetaChip>
            <MetaChip tone="blue">{cls}</MetaChip>
            <MetaChip tone="green">Roll: {rollNo}</MetaChip>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 6,
            }}
          >
            <div style={tile}>
              <div style={label}>Father</div>
              <div style={value} title={student.father_name || "—"}>
                {student.father_name || "—"}
              </div>
            </div>
            <div style={{ ...tile, borderColor: COLORS.primary_color_shade3 }}>
              <div style={{ ...label, color: ACCENT_DARK }}>Class</div>
              <div style={{ ...value, color: ACCENT_DARK }} title={cls}>
                {cls}
              </div>
            </div>
            <div style={{ ...tile, borderColor: COLORS.primary_color_shade3 }}>
              <div style={{ ...label, color: ACCENT_DARK }}>Roll No</div>
              <div style={{ ...value, color: ACCENT_DARK }}>{rollNo}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function formatMonthLabel(value) {
  if (!value) return "—";
  const m = String(value).match(/^(\d{4})-(\d{2})$/);
  if (!m) return String(value);
  const names = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const idx = parseInt(m[2], 10) - 1;
  return names[idx] ? `${names[idx]} ${m[1]}` : String(value);
}

//Created By Cursor
const FeeLinesTable = memo(function FeeLinesTable({ items, transactionFor, netPaid }) {
  const isOldDue = transactionFor === "OLD_DUE";

  const thStyle = {
    padding: "10px 14px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: COLORS.white,
    whiteSpace: "nowrap",
    textAlign: "left",
    border: "none",
  };

  const tdStyle = {
    padding: "9px 14px",
    fontSize: 13,
    color: "#0f172a",
    verticalAlign: "middle",
    borderBottom: "1px solid #e2e8f0",
  };

  const amountStyle = {
    ...tdStyle,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        borderRadius: 14,
        border: `1.5px solid ${COLORS.primary_color_shade3}`,
        overflow: "hidden",
        background: COLORS.white,
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
              }}
            >
              <th style={{ ...thStyle, width: 56, textAlign: "center" }}>#</th>
              <th style={thStyle}>{isOldDue ? "Description" : "Fee Description"}</th>
              <th style={thStyle}>{isOldDue ? "Session" : "Month"}</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Payable</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Paid</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const title = isOldDue ? "Old Due" : item.fee_name || "Fee Item";
              const period = isOldDue
                ? item.session_name || "—"
                : formatMonthLabel(item.month || item.year_month);
              const payable = isOldDue
                ? Number(item.amount_payable) || 0
                : Number(item.fee_payable) || 0;
              const paid = lineAmount(item, transactionFor);
              const zebra = index % 2 === 1;

              return (
                <tr
                  key={`${item.transaction_id || "txn"}-${item.fee_id || item.due_id || "line"}-${item.year_month || ""}-${index}`}
                  style={{ background: zebra ? "#f8fafc" : COLORS.white, transition: "background 0.12s ease" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = COLORS.primary_color_shade0;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = zebra ? "#f8fafc" : COLORS.white;
                  }}
                >
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                        borderRadius: 8,
                        background: COLORS.primary_color_shade0,
                        border: `1px solid ${COLORS.primary_color_shade3}`,
                        color: ACCENT_DARK,
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      {index + 1}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 800 }}>
                    {title}
                    {isOldDue && item.remark ? (
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#9a3412", marginTop: 2 }}>
                        Remark: {item.remark}
                      </div>
                    ) : null}
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        fontWeight: 700,
                        color: ACCENT_DARK,
                        background: COLORS.primary_color_shade0,
                        border: `1px solid ${COLORS.primary_color_shade3}`,
                        borderRadius: 999,
                        padding: "2px 10px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <i className="fa fa-calendar-o" style={{ fontSize: 10 }} aria-hidden />
                      {period}
                    </span>
                  </td>
                  <td style={{ ...amountStyle, color: "#64748b", fontWeight: 600 }}>
                    {formatAmount(payable)}
                  </td>
                  <td style={{ ...amountStyle, fontWeight: 800 }}>{formatAmount(paid)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: COLORS.primary_color_shade0 }}>
              <td
                colSpan={4}
                style={{
                  padding: "11px 14px",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: ACCENT_DARK,
                  textAlign: "right",
                  borderTop: `1.5px solid ${COLORS.primary_color_shade3}`,
                }}
              >
                Net Paid
              </td>
              <td
                style={{
                  padding: "11px 14px",
                  fontSize: 15,
                  fontWeight: 800,
                  color: ACCENT_DARK,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  borderTop: `1.5px solid ${COLORS.primary_color_shade3}`,
                }}
              >
                {formatAmount(netPaid)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
});


const PaymentDetails = () => {
  const {
    transaction_id: transactionId,
    session_id: sessionId,
    student_id: studentId,
    transaction_for: transactionFor = "FEE",
  } = useParams();

  const loggedIn = localStorage.getItem("token") !== null;

  const [loading, setLoading] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [studentDetails, setStudentDetails] = useState([]);

  const student = studentDetails[0] || null;
  const theme = getTypeTheme(transactionFor);

  const getPaymentDetails = useCallback(() => {
    if (!transactionId || !sessionId) return;

    setLoading(true);
    const functionName =
      transactionFor === "OLD_DUE"
        ? "getOldDuePaymentDetails.php"
        : "getPaymentDetails.php";

    axios
      .get(`${API_BASE()}${functionName}`, {
        params: {
          school_code: SessionManager.getSchoolCode(),
          session_id: sessionId,
          transaction_id: transactionId,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          setStudentDetails(response.data?.[0]?.student_details || []);
          setPaymentDetails(response.data?.[0]?.payment_details || []);
        } else {
          setStudentDetails([]);
          setPaymentDetails([]);
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log("getPaymentDetails error:", error);
      });
  }, [transactionId, sessionId, transactionFor]);

  useEffect(() => {
    getPaymentDetails();
  }, [getPaymentDetails]);

  const netPaidAmount = useMemo(() => {
    return (paymentDetails || []).reduce(
      (sum, item) => sum + lineAmount(item, transactionFor),
      0
    );
  }, [paymentDetails, transactionFor]);

  const firstLine = paymentDetails[0] || null;
  const receiptTo = `/AdminMain/FeeReceipt/${transactionId}/${studentId}/${sessionId}`;

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (loading && paymentDetails.length === 0) {
    return <MyLoader />;
  }

  return (
    <div className="content" style={{ background: COLORS.white }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#" style={{ color: COLORS.white }}>
                Payment Details
              </a>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "0",
          padding: 0,
          overflow: "hidden",
          borderRadius: 14,
        }}
      >
        <div
          style={{
            background: HEADER_GRADIENT,
            color: "#fff",
            padding: "12px 40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            minHeight: 56,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="fa fa-receipt" style={{ fontSize: 18 }} aria-hidden />
            </div>
            <div>
              <h5 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                Payment Details
              </h5>
              <small style={{ display: "block", marginTop: 2, fontSize: 12, opacity: 0.92 }}>
                {paymentDetails.length} fee line
                {paymentDetails.length === 1 ? "" : "s"}
              </small>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "stretch",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            {[
              { label: "Transaction", value: transactionId },
              { label: "Type", value: theme.label },
              { label: "Txn Date", value: firstLine?.transaction_date || "—" },
              { label: "Collection", value: firstLine?.collection_date || "—" },
            ].map((chip) => (
              <div
                key={chip.label}
                style={{
                  padding: "8px 14px",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.4)",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    opacity: 0.85,
                  }}
                >
                  {chip.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, marginTop: 1 }}>
                  {chip.value}
                </div>
              </div>
            ))}

            <div
              style={{
                padding: "8px 14px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.4)",
                textAlign: "right",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  opacity: 0.85,
                }}
              >
                Net Paid
              </div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>
                {formatAmount(netPaidAmount)}
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "16px 40px 24px" }}>
          {loading && (
            <div style={{ textAlign: "center", padding: 20 }}>
              <MyLoader />
            </div>
          )}

          {!loading && <StudentSummary student={student} />}

          {!loading && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 8,
                      background: ACCENT,
                      color: COLORS.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                    }}
                  >
                    <i className="fa fa-list" aria-hidden />
                  </div>
                  <h5
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      color: "#1e293b",
                    }}
                  >
                    Fee Lines
                  </h5>
                </div>
                <MetaChip tone="blue">{paymentDetails.length} items</MetaChip>
              </div>

              {paymentDetails.length === 0 ? (
                <div
                  style={{
                    padding: "28px 16px",
                    borderRadius: 12,
                    background: "#f8fafc",
                    border: "1px dashed #cbd5e1",
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: 13,
                  }}
                >
                  No payment line items found for this transaction.
                </div>
              ) : (
                <FeeLinesTable
                  items={paymentDetails}
                  transactionFor={transactionFor}
                  netPaid={netPaidAmount}
                />
              )}

              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 14,
                  border: `1.5px solid ${COLORS.primary_color_shade3}`,
                  background: `linear-gradient(135deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.white} 70%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: "#64748b",
                    }}
                  >
                    Ready to print
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    Net Paid {formatAmount(netPaidAmount)}
                  </div>
                </div>
                <Link to={receiptTo} style={{ textDecoration: "none" }}>
                  <button
                    type="button"
                    style={{
                      border: "none",
                      borderRadius: 10,
                      padding: "11px 18px",
                      background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
                      color: COLORS.white,
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: "pointer",
                      boxShadow: `0 4px 14px ${ACCENT_SHADOW}`,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <i className="fa fa-file-text-o" aria-hidden />
                    Get Fee Receipt
                  </button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
